import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  MessageSquare,
  Settings,
  Save,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Phone,
  Key,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  Snowflake,
  Building2,
  Eye,
  EyeOff
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fluxoco_token';

const WhatsAppConfigPage = () => {
  const [tenants, setTenants] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    waba_id: '',
    phone_number_id: '',
    phone_number: '',
    access_token: '',
    is_sandbox: true,
    test_mode: true
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [tenantsRes, configsRes, logsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tenants`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/whatsapp/configs`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/whatsapp/logs?limit=50`, { headers: getAuthHeaders() })
      ]);
      setTenants(tenantsRes.data);
      setConfigs(configsRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // If endpoints don't exist yet, just set empty
      setConfigs([]);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const openConfigDialog = (tenant) => {
    setSelectedTenant(tenant);
    const existingConfig = configs.find(c => c.tenant_id === tenant.id);
    if (existingConfig) {
      setFormData({
        waba_id: existingConfig.waba_id || '',
        phone_number_id: existingConfig.phone_number_id || '',
        phone_number: existingConfig.phone_number || '',
        access_token: existingConfig.access_token || '',
        is_sandbox: existingConfig.is_sandbox ?? true,
        test_mode: existingConfig.test_mode ?? true
      });
    } else {
      setFormData({
        waba_id: '',
        phone_number_id: '',
        phone_number: '',
        access_token: '',
        is_sandbox: true,
        test_mode: true
      });
    }
    setIsConfigOpen(true);
  };

  const saveConfig = async () => {
    if (!formData.waba_id || !formData.phone_number_id || !formData.access_token) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/whatsapp/configs`, {
        tenant_id: selectedTenant.id,
        ...formData
      }, { headers: getAuthHeaders() });
      
      toast.success(`Configuración de WhatsApp guardada para ${selectedTenant.name}`);
      setIsConfigOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const getStatusBadge = (config) => {
    if (!config) return <Badge variant="outline">No configurado</Badge>;
    if (config.is_sandbox) return <Badge className="bg-amber-500">Sandbox</Badge>;
    return <Badge className="bg-green-500">Producción</Badge>;
  };

  const webhookUrl = `${API_URL}/api/whatsapp/webhook`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="whatsapp-config-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit tracking-tight">
            Configuración WhatsApp
          </h1>
          <p className="text-muted-foreground mt-1">
            Configura la integración de WhatsApp Business API por tenant
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      <Tabs defaultValue="tenants">
        <TabsList>
          <TabsTrigger value="tenants">Tenants</TabsTrigger>
          <TabsTrigger value="webhook">Webhook</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="guide">Guía Meta</TabsTrigger>
        </TabsList>

        {/* Tenants Tab */}
        <TabsContent value="tenants" className="space-y-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>WhatsApp</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Modo Pruebas</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenant) => {
                  const config = configs.find(c => c.tenant_id === tenant.id);
                  const TenantIcon = tenant.name?.toLowerCase().includes('hielo') ? Snowflake : Building2;
                  
                  return (
                    <TableRow key={tenant.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            tenant.name?.toLowerCase().includes('hielo')
                              ? 'bg-gradient-to-br from-cyan-500 to-blue-600'
                              : 'bg-gradient-to-br from-violet-500 to-purple-600'
                          }`}>
                            <TenantIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium">{tenant.name}</p>
                            <p className="text-sm text-muted-foreground">{tenant.whatsapp_principal}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {config ? (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-green-500" />
                            <span className="text-sm">{config.phone_number || 'Configurado'}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">No configurado</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(config)}
                      </TableCell>
                      <TableCell>
                        {config?.test_mode ? (
                          <Badge variant="outline" className="text-amber-500 border-amber-500">ON</Badge>
                        ) : (
                          <Badge variant="outline">OFF</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => openConfigDialog(tenant)}>
                          <Settings className="w-4 h-4 mr-2" />
                          Configurar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Webhook Tab */}
        <TabsContent value="webhook" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Webhook URL</CardTitle>
              <CardDescription>
                Registra esta URL en tu configuración de WhatsApp Business API en Meta
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground">Callback URL</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-sm font-mono text-cyan-400 break-all">{webhookUrl}</code>
                  <Button size="icon" variant="ghost" onClick={() => copyToClipboard(webhookUrl)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground">Verify Token</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 text-sm font-mono">fluxoco_webhook_verify_2024</code>
                  <Button size="icon" variant="ghost" onClick={() => copyToClipboard('fluxoco_webhook_verify_2024')}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <p className="text-sm text-amber-500 font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Importante
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Debes configurar este webhook en Meta Business Suite bajo WhatsApp → Configuración → Webhook.
                  Selecciona los eventos: messages, message_status.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Logs de WhatsApp</CardTitle>
              <CardDescription>
                Últimos eventos de WhatsApp recibidos
              </CardDescription>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay logs todavía</p>
                  <p className="text-sm">Los eventos de WhatsApp aparecerán aquí</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {logs.map((log, index) => (
                    <div key={index} className="p-3 bg-muted rounded-lg text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">{log.type}</span>
                        <span className="text-xs text-muted-foreground">{log.timestamp}</span>
                      </div>
                      <pre className="text-xs text-muted-foreground overflow-x-auto">
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guide Tab */}
        <TabsContent value="guide" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Guía: Crear cuenta WhatsApp Business API</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold shrink-0">1</div>
                  <div>
                    <h3 className="font-medium">Crear cuenta en Meta for Developers</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ve a <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">developers.facebook.com</a> y crea una cuenta de desarrollador.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold shrink-0">2</div>
                  <div>
                    <h3 className="font-medium">Crear una App</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      En el panel de desarrolladores, crea una nueva app seleccionando "Business" como tipo.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold shrink-0">3</div>
                  <div>
                    <h3 className="font-medium">Agregar WhatsApp a tu App</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      En el dashboard de tu app, busca "WhatsApp" y haz clic en "Set Up".
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold shrink-0">4</div>
                  <div>
                    <h3 className="font-medium">Verificar número de teléfono</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      En la configuración de WhatsApp, agrega y verifica el número de teléfono de tu negocio.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold shrink-0">5</div>
                  <div>
                    <h3 className="font-medium">Obtener credenciales</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Copia tu <strong>WABA_ID</strong>, <strong>Phone Number ID</strong> y genera un <strong>Access Token permanente</strong> desde System Users.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold shrink-0">6</div>
                  <div>
                    <h3 className="font-medium">Configurar Webhook</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      En la sección de Webhooks, pega la URL de Fluxo Co y el Verify Token que aparecen arriba.
                    </p>
                  </div>
                </div>
              </div>

              <Button asChild className="w-full">
                <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ir a Meta Business Suite
                </a>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Config Dialog */}
      <Dialog open={isConfigOpen} onOpenChange={setIsConfigOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              Configurar WhatsApp - {selectedTenant?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="waba_id">WABA ID *</Label>
              <Input
                id="waba_id"
                value={formData.waba_id}
                onChange={(e) => setFormData({ ...formData, waba_id: e.target.value })}
                placeholder="Ej: 123456789012345"
                className="bg-slate-950/50 border-white/10 rounded-xl focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number_id">Phone Number ID *</Label>
              <Input
                id="phone_number_id"
                value={formData.phone_number_id}
                onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
                placeholder="Ej: 987654321098765"
                className="bg-slate-950/50 border-white/10 rounded-xl focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">Número de Teléfono</Label>
              <Input
                id="phone_number"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                placeholder="Ej: +52 55 1234 5678"
                className="bg-slate-950/50 border-white/10 rounded-xl focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="access_token">Access Token *</Label>
              <div className="relative">
                <Input
                  id="access_token"
                  type={showToken ? 'text' : 'password'}
                  value={formData.access_token}
                  onChange={(e) => setFormData({ ...formData, access_token: e.target.value })}
                  placeholder="Token de acceso permanente"
                  className="pr-12 bg-slate-950/50 border-white/10 rounded-xl focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full text-slate-500 hover:text-emerald-400"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <Label>Modo Sandbox</Label>
                <p className="text-xs text-muted-foreground">Solo para pruebas con números limitados</p>
              </div>
              <Switch
                checked={formData.is_sandbox}
                onCheckedChange={(checked) => setFormData({ ...formData, is_sandbox: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <Label>Modo Pruebas Hielo Rey</Label>
                <p className="text-xs text-muted-foreground">Activa logging detallado y flujos de prueba</p>
              </div>
              <Switch
                checked={formData.test_mode}
                onCheckedChange={(checked) => setFormData({ ...formData, test_mode: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfigOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveConfig} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsAppConfigPage;
